import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  ArrowRight,
  ArrowLeft,
  Phone,
  Wrench,
  BarChart3,
  Briefcase,
  Smartphone,
  Mail,
  Loader2,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import mynaLogo from "@/assets/myna-logo.svg";
import {
  signInWithGoogle,
  signInWithEmail,
  signInWithPassword,
} from "@/lib/auth";

type PortalType = "select" | "business" | "technician" | "sales";
type AuthMode = "social" | "email" | "password" | "magic-link-sent";

const portals = [
  {
    id: "business" as const,
    title: "Business Owner",
    description: "Full dashboard access - manage calls, bookings, campaigns, and team",
    icon: Phone,
    features: ["Business dashboard", "Tech portal access", "Team management", "Full analytics"],
    accessNote: "Owners & single operators get access to both business dashboard and tech app",
    route: "/app/dashboard",
  },
  {
    id: "technician" as const,
    title: "Technician",
    description: "Mobile-first app for field work - jobs, equipment surveys, and voice notes",
    icon: Wrench,
    features: ["Today's jobs", "One-tap ETA", "Equipment survey", "Voice notes"],
    mobileHint: "Optimized for mobile - works offline",
    accessNote: "Tech-only users access field app",
    route: "/app/tech",
  },
];

// Sales portal config (discrete link, not shown as card)
const SALES_PORTAL = {
  id: "sales" as const,
  title: "Sales Team",
  description: "Internal sales portal - demos, pipeline, and customer onboarding",
  icon: Briefcase,
  route: "/sales",
};

// Social login provider icons
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}



const Login = () => {
  const navigate = useNavigate();
  const [selectedPortal, setSelectedPortal] = useState<PortalType>("select");
  const [authMode, setAuthMode] = useState<AuthMode>("social");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const currentPortal = selectedPortal === "sales"
    ? SALES_PORTAL
    : portals.find((p) => p.id === selectedPortal);

  const handleGoogleLogin = async () => {
    setLoadingProvider("google");
    try {
      if (currentPortal?.route) {
        localStorage.setItem("auth_redirect_to", currentPortal.route);
      }

      await signInWithGoogle();
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error("Google login error:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Google login failed: ${errorMessage}`);
      setLoadingProvider(null);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      if (currentPortal?.route) {
        localStorage.setItem("auth_redirect_to", currentPortal.route);
      }

      await signInWithEmail(email);
      setAuthMode("magic-link-sent");
      toast.success("Check your email for the magic link!");
    } catch (error) {
      if (import.meta.env.DEV) console.error("Magic link error:", error);
      toast.error("Failed to send magic link");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);
    try {
      await signInWithPassword(email, password);
      const targetRoute = currentPortal?.route || "/";
      navigate(targetRoute);
    } catch (error) {
      if (import.meta.env.DEV) console.error("Password login error:", error);
      toast.error("Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (authMode !== "social") {
      setAuthMode("social");
    } else {
      setSelectedPortal("select");
    }
    setEmail("");
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-[#292929] text-white">
      {/* Gradient wash background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#2e2e2e] via-[#292929] to-[#282828]" />
      </div>

      {/* Navbar */}
      <nav className="relative z-50 border-b border-white/[0.1]">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 py-5">
          <div className="flex items-center justify-between">
            <Link to="/">
              <img src={mynaLogo} alt="MYNA AI" className="h-16 w-auto opacity-90" />
            </Link>
            <Link to="/">
              <button className="px-5 py-2.5 text-sm text-gray-300 hover:text-white transition-colors font-medium">
                Back to Home
              </button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative z-10 pt-20 pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {selectedPortal === "select" ? (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
                  Welcome to Myna
                </h1>
                <p className="text-xl text-gray-400 mb-2">
                  Are you a business owner or field technician?
                </p>
                <p className="text-sm text-gray-500">
                  Single operators get access to both portals automatically
                </p>
              </>
            ) : (
              <>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
                  {currentPortal?.title}
                </h1>
                <p className="text-xl text-gray-400">{currentPortal?.description}</p>
              </>
            )}
          </motion.div>

          {selectedPortal === "select" ? (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
              >
              {portals.map((portal, index) => (
                <motion.button
                  key={portal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
                  onClick={() => setSelectedPortal(portal.id)}
                  className={`group relative p-8 bg-gradient-to-br from-[#2e2e2e] to-[#2d2d2d] border-2 transition-all text-left shadow-xl hover:shadow-2xl hover:-translate-y-2 ${
                    index === 0
                      ? "border-lime-500/30 hover:border-lime-500/50 hover:shadow-lime-500/20"
                      : "border-teal-500/30 hover:border-teal-500/50 hover:shadow-teal-500/20"
                  }`}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 border-2 flex items-center justify-center flex-shrink-0 ${
                        index === 0
                          ? "bg-lime-500/15 border-lime-500/40"
                          : "bg-teal-500/15 border-teal-500/40"
                      }`}>
                        <portal.icon className={`w-7 h-7 ${
                          index === 0 ? "text-lime-400" : "text-teal-400"
                        }`} strokeWidth={2} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-xl mb-1 transition-colors ${
                          index === 0
                            ? "text-gray-100 group-hover:text-lime-400"
                            : "text-gray-100 group-hover:text-teal-400"
                        }`}>
                          {portal.title}
                        </h3>
                      </div>
                      <ArrowRight className={`w-6 h-6 transition-all ${
                        index === 0
                          ? "text-gray-500 group-hover:text-lime-400 group-hover:translate-x-1"
                          : "text-gray-500 group-hover:text-teal-400 group-hover:translate-x-1"
                      }`} />
                    </div>

                    <p className="text-sm text-gray-400 leading-relaxed">
                      {portal.description}
                    </p>

                    <div className={`p-3 text-xs italic border-l-2 ${
                      index === 0
                        ? "bg-lime-500/5 border-lime-500/30 text-gray-500"
                        : "bg-teal-500/5 border-teal-500/30 text-gray-500"
                    }`}>
                      {portal.accessNote}
                    </div>

                    <div className="flex flex-wrap gap-2 mt-2">
                      {portal.features.map((feature) => (
                        <span
                          key={feature}
                          className="text-xs px-2 py-1 bg-white/5 border border-white/10 text-gray-500"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {portal.mobileHint && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-teal-400">
                        <Smartphone className="w-3 h-3" />
                        {portal.mobileHint}
                      </div>
                    )}
                  </div>
                </motion.button>
              ))}
            </motion.div>

            {/* Sales portal - small link at bottom */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center mt-8"
            >
              <button
                onClick={() => setSelectedPortal("sales")}
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                Internal Sales Team →
              </button>
            </motion.div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-md mx-auto"
            >
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {authMode !== "social" ? "Back to login options" : "Back to portal selection"}
              </button>

              <div className="bg-gradient-to-br from-[#2e2e2e] to-[#2d2d2d] border-2 border-white/[0.15] p-8 shadow-2xl">
                {/* Portal indicator */}
                <div className="flex items-center gap-3 mb-8 pb-6 border-b border-white/[0.08]">
                  <div className="w-10 h-10 bg-lime-500/15 border-2 border-lime-500/40 flex items-center justify-center">
                    {currentPortal && <currentPortal.icon className="w-5 h-5 text-lime-400" strokeWidth={2} />}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-100">{currentPortal?.title}</p>
                    <p className="text-xs text-gray-500">Secure login</p>
                  </div>
                </div>

                {authMode === "magic-link-sent" ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 bg-teal-500/10 border-2 border-teal-500/40 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-teal-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-100 mb-2">Check your email</h3>
                    <p className="text-gray-400 mb-4">
                      We sent a magic link to <strong className="text-gray-200">{email}</strong>
                    </p>
                    <p className="text-sm text-gray-500">
                      Click the link in the email to sign in. The link expires in 1 hour.
                    </p>
                    <button
                      onClick={() => setAuthMode("social")}
                      className="mt-6 text-sm text-gray-400 hover:text-gray-200 transition-colors"
                    >
                      Use a different method
                    </button>
                  </div>
                ) : authMode === "social" ? (
                  <div className="space-y-3">
                    {/* Social Login Buttons */}
                    <button
                      onClick={handleGoogleLogin}
                      disabled={loadingProvider !== null}
                      className="w-full flex items-center justify-start gap-3 px-6 py-3 bg-[#333333] border-2 border-white/20 text-gray-200 hover:bg-[#3a3a3a] hover:border-white/30 transition-all disabled:opacity-50"
                    >
                      {loadingProvider === "google" ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <GoogleIcon className="w-5 h-5" />
                      )}
                      <span className="font-medium">Continue with Google</span>
                    </button>

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-white/[0.08]" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-[#2e2e2e] px-2 text-gray-500">or</span>
                      </div>
                    </div>

                    <button
                      onClick={() => setAuthMode("email")}
                      className="w-full flex items-center justify-start gap-3 px-6 py-3 bg-transparent border-2 border-white/20 text-gray-300 hover:bg-white/5 hover:border-white/30 transition-all"
                    >
                      <Mail className="w-5 h-5" />
                      <span className="font-medium">Continue with Email</span>
                    </button>
                  </div>
                ) : authMode === "email" ? (
                  <form onSubmit={handleMagicLink} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-200">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-[#333333] border-white/20 text-gray-100 placeholder:text-gray-500"
                        required
                        autoFocus
                      />
                    </div>

                    <Button
                      className="w-full px-6 py-4 bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] font-bold hover:from-lime-600 hover:to-lime-500 shadow-lg shadow-lime-500/30"
                      type="submit"
                      disabled={isLoading || !email}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Magic Link
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setAuthMode("password")}
                        className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                      >
                        Use password instead
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handlePasswordLogin} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-200">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-[#333333] border-white/20 text-gray-100 placeholder:text-gray-500"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <Label htmlFor="password" className="text-gray-200">Password</Label>
                        <Link
                          to="/forgot-password"
                          className="text-sm text-lime-400 hover:text-lime-300 transition-colors"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="bg-[#333333] border-white/20 text-gray-100 placeholder:text-gray-500 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <Button
                      className="w-full px-6 py-4 bg-gradient-to-r from-lime-500 to-lime-400 text-[#1f1f1f] font-bold hover:from-lime-600 hover:to-lime-500 shadow-lg shadow-lime-500/30"
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign in
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setAuthMode("email")}
                        className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
                      >
                        Use magic link instead
                      </button>
                    </div>
                  </form>
                )}

                {selectedPortal === "technician" && authMode === "social" && (
                  <div className="mt-6 p-4 bg-teal-500/5 border border-teal-500/20">
                    <p className="text-sm text-gray-400 text-center">
                      <Smartphone className="w-4 h-4 inline mr-1 text-teal-400" />
                      For the best mobile experience, add this app to your home screen after signing in.
                      Works offline for viewing jobs!
                    </p>
                  </div>
                )}

                {authMode === "social" && (
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-400">
                      Don't have an account?{" "}
                      <Link to="/demo" className="text-lime-400 hover:text-lime-300 transition-colors">
                        Request a demo
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center text-sm text-gray-500 mt-8"
          >
            By signing in, you agree to our{" "}
            <Link to="/terms" className="text-lime-400 hover:text-lime-300 transition-colors">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-lime-400 hover:text-lime-300 transition-colors">
              Privacy Policy
            </Link>
          </motion.p>
        </div>
      </main>
    </div>
  );
};

export default Login;
